import { useMutation } from '@apollo/client/react';
import type { Reference } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import {
    CreateArticleDocument,
    ICreateLibraryArticleInput,
} from 'graphql/types';
import { PageDrawer } from 'common/components/PageDrawer';
import { LibraryArticleForm } from 'library/components/forms/LibraryArticleForm';
import { ApolloError } from 'common/components/ApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { PageTitle } from 'common/components/PageTitle';
import { getImageId } from 'common/utils/image';
import { formatColor } from 'common/utils/color';

interface IProps {
    pageTitle?: string;
    open?: boolean;
    parentId: string;
    onCreate?(articleId: string): void;
    onClose?(): void;
}

export const LibraryArticleCreateDrawer = ({
    pageTitle,
    open,
    parentId,
    onCreate,
    onClose,
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();

    const [createArticle, { error: createError }] = useMutation(
        CreateArticleDocument,
        {
            update: (cache, { data }) => {
                const newArticle = data?.createLibraryArticle?.article;

                if (!newArticle) return;

                // Mutation already wrote the article; append its ref to the list.
                // Keep snackbar/onCreate out of update so a cache miss cannot
                // block redirect to the article editor.
                cache.modify({
                    id: `LibraryCollection:${parentId}`,
                    fields: {
                        articles(
                            existing: readonly Reference[] | null | undefined,
                            { readField, toReference }
                        ) {
                            // articles is nullable, so a default value is not
                            // enough: a cached null would throw below.
                            const existingRefs = existing ?? [];
                            const newRef = toReference(newArticle);

                            if (!newRef) {
                                return existingRefs;
                            }

                            if (
                                existingRefs.some(
                                    (ref: Reference) =>
                                        readField('id', ref) === newArticle.id
                                )
                            ) {
                                return existingRefs;
                            }

                            return [...existingRefs, newRef];
                        },
                    },
                });
            },
        }
    );

    const initialValues = {
        title: '',
        description: '',
        featured: false,
        titleColor: '',
    };

    return (
        <PageDrawer open={open} onClose={onClose}>
            {createError && <ApolloError error={createError} />}

            {pageTitle && (
                <PageTitle>{`${pageTitle} - ${translate(
                    'newArticle'
                )}`}</PageTitle>
            )}

            <Box sx={{ p: { xs: 2, sm: 4 } }}>
                <Box sx={{ mb: 4 }}>
                    <Typography sx={{ fontWeight: 'bold', mt: 0 }} variant="h3">
                        {translate('createArticle')}
                    </Typography>
                </Box>

                <LibraryArticleForm
                    initialValues={initialValues}
                    onClose={onClose}
                    onSubmit={async (values) => {
                        const {
                            thumbnail,
                            headerImage,
                            titleColor,
                            ...articleInput
                        } = values;

                        const newArticle = {
                            ...articleInput,
                            titleColor: formatColor(titleColor),
                            thumbnailId: getImageId(undefined, thumbnail),
                            headerImageId: getImageId(undefined, headerImage),
                            collectionId: parentId,
                        } as ICreateLibraryArticleInput;

                        const result = await createArticle({
                            variables: {
                                article: newArticle,
                            },
                        });

                        const created =
                            result.data?.createLibraryArticle?.article;

                        if (!created) {
                            return;
                        }

                        displaySnackbar(
                            translate('libraryArticleActionSuccess.create'),
                            {
                                variant: 'success',
                            }
                        );

                        onCreate?.(created.id);
                    }}
                />
            </Box>
        </PageDrawer>
    );
};
